import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { useContext } from "react";
import { ReactElement } from "react";

import List from "./List";
import { CommentsContext } from "../context";
import { IComment } from "../types";
import { Icon24ChatMessage } from "../../Icons";
import { IMarker, ISelection } from "../../RichEditor/RichEditor";

interface IThreadProps {
  name: string;
  useGrid?: boolean;
  order?: string[];
  children: (props: IThreadChildrenProps) => ReactElement;
}

interface IThreadChildrenProps {
  markers: IMarker[];
  AddCommentIcon: ReactElement;
  addCommentForMarker: (selection: ISelection) => void;
  showMarker: (id: string) => void;
  addComment: () => void;
}

export function Thread(props: IThreadProps) {
  const ctx = useContext(CommentsContext);
  const { useGrid = true, order } = props;
  const { threads } = ctx;
  const comments = threads[props.name] ?? [];

  // Collect all comments (root + child) for stack display
  const getAllComments = (commentList: IComment[]): IComment[] => {
    const allComments: IComment[] = [];
    const seenIds = new Set<string>();

    const addComment = (comment: IComment) => {
      if (seenIds.has(comment.id)) {
        return;
      }
      seenIds.add(comment.id);
      allComments.push(comment);
    };

    commentList.forEach((comment) => {
      addComment(comment);
      if (comment.replies && comment.replies.length > 0) {
        comment.replies.forEach((reply) => addComment(reply));
      }
    });

    // Sort by creation time (newest first, since List.tsx has .reverse())
    const sortedComments = allComments.sort((a, b) => {
      if (a.created && b.created) {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
      }
      return 0;
    });

    return sortedComments;
  };

  const isThreadExpanded = ctx.showedThreadName === props.name;

  // In closed state show all comments for stack
  // In open state use only root comments (with nested)
  const allCommentsForStack = getAllComments(comments);

  const isSomeThreadExpanded = Boolean(ctx.showedThreadName);

  const orderMap = order?.reduce((acc, item, index) => {
    acc[item] = index;
    return acc;
  }, {} as Record<string, number>);

  const _sortedComments = order?.length
    ? comments.sort((a, b) => {
        if (orderMap) {
          const aIndex = orderMap[a.id];
          const bIndex = orderMap[b.id];
          if (aIndex !== undefined && bIndex !== undefined) {
            return bIndex - aIndex;
          }
          if (aIndex !== undefined) return 1;
          if (bIndex !== undefined) return -1;
        }

        if (a.created && b.created) {
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        }
        return 0;
      })
    : comments;

  if (ctx.hidden) {
    return (
      <>
        {props.children({
          markers: [],
          AddCommentIcon: <></>,
          addCommentForMarker: () => null,
          showMarker: () => null,
          addComment: () => null,
        })}
      </>
    );
  }

  return (
    <div
      className={clsxm(
        useGrid
          ? "w-full flex-grow sm:grid sm:grid-cols-6 lg:mx-auto lg:grid-cols-12 lg:gap-8"
          : "flex"
      )}
    >
      <div className="col-span-7 w-full min-w-full flex-initial">
        {props.children({
          markers: comments
            .filter((item: IComment) => !!item.selection)
            .filter((item: IComment) => ctx.isManager || !item.isResolved)
            .map((item: IComment) => ({
              id: item.id,
              color: item.color ? item.color : "",
            })),
          AddCommentIcon: ctx.isManager ? (
            <Icon24ChatMessage
              className="text-black-400"
              onClick={() => {
                ctx.setShowedThreadName && ctx.setShowedThreadName("");
                ctx.addComment && ctx.addComment(props.name);
              }}
            />
          ) : (
            <></>
          ),
          addComment: () => {
            ctx.addComment && ctx.addComment(props.name);
          },
          addCommentForMarker: (selection) => {
            if (!ctx.isManager) {
              return;
            }
            if (selection.to - selection.from === 0) {
              return;
            }
            ctx.addComment && ctx.addComment(props.name, selection);
          },
          showMarker: (id: string) => {
            ctx.setFocusedCommentId && ctx.setFocusedCommentId(id);
          },
        })}
      </div>
      <div
        className={clsxm(
          "bg-white relative col-span-5 flex-initial",
          isThreadExpanded ? "" : ""
        )}
      >
        <List
          useGrid={useGrid}
          key={`${props.name}-${ctx.hash}`}
          onResolve={(id: string, state) =>
            ctx.resolveComment?.(props.name, id, state)
          }
          focusedCommentId={ctx.focusedCommentId}
          isShowed={isThreadExpanded}
          isBlured={isSomeThreadExpanded}
          onShowToggled={(state: boolean) => {
            if (state) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  ctx.setShowedThreadName?.(props.name);
                });
              });
            } else {
              ctx.setShowedThreadName?.("");
            }
          }}
          comments={allCommentsForStack}
        />
      </div>
    </div>
  );
}

export function GlobalThread(props: { name: string; className?: string }) {
  const ctx = useContext(CommentsContext);
  const { threads } = ctx;
  const comments = threads[props.name] ?? [];

  return (
    <List
      className={clsxm(props.className ?? "")}
      isGlobal={true}
      useGrid={false}
      key={props.name}
      onResolve={(id: string, state) =>
        ctx.resolveComment && ctx.resolveComment(props.name, id, state)
      }
      focusedCommentId={ctx.focusedCommentId}
      isShowed={ctx.showedThreadName === props.name}
      onShowToggled={(state: boolean) => {
        ctx.setShowedThreadName?.(state ? props.name : "");
      }}
      comments={comments}
    />
  );
}

export function ThreadFree(props: { children: ReactElement }) {
  return (
    <div className="flex">
      <div className="w-full flex-initial">{props.children}</div>
    </div>
  );
}
